import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { DEFAULT_AGENT_GROUNDING } from '@/constants/agent/defaults';
import ArrowDown from '@/images/ArrowDown.svg';
import { useAnalytics } from '@/services/analytics';
import { getDefaultTemperature } from '@/state/agent';
import { useAgentEditorStore } from '@/state/root';
import theme from '@d-id/studio/src/theme';
import { AgentCreateTabType, AgentGrounding } from '@d-id/types';
import styled from '@emotion/styled';
import { ClickAwayListener, Tooltip, TooltipProps } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';
import { GroundingList } from './GroundingList';
import { GroundingOption } from './GroundingOption';

const slotProps: TooltipProps['slotProps'] = {
    popper: {
        disablePortal: true,
        sx: {
            width: '100%',
        },
        modifiers: [{ name: 'offset', options: { offset: [0, 4] } }],
    },
    tooltip: {
        sx: {
            borderRadius: '8px',
            border: `1px solid ${theme.colors.secondary[200]}`,
            background: 'white',
            minWidth: '100% !important',
            margin: '0 !important',
            padding: 0,
            boxShadow:
                '5.3px 13.3px 18.8px -1.2px rgba(187, 187, 187, 0.29), 2.4px 6.1px 8.6px -0.8px rgba(187, 187, 187, 0.23), 1.1px 2.8px 3.9px -0.4px rgba(187, 187, 187, 0.17), 0.3px 0.8px 1.1px 0px rgba(187, 187, 187, 0.12)',
        },
    },
};

const TooltipContainer = styled.div`
    width: 100%;
    position: relative;
`;

const TriggerContainer = styled.div`
    border: 1px solid ${({ theme }) => theme.colors.secondary[200]};
    border-radius: 8px;

    width: 100%;
`;

const StyledIconButton = styled(IconButton)`
    display: flex;
    justify-content: start;

    width: 100%;
    padding: 12px;
    border-radius: 8px;

    &:focus {
        outline: none;
    }

    &:disabled {
        opacity: 0.5;
    }
    &:hover {
        background-color: ${({ theme }) => theme.colors.black[100]};
    }
`;

const MaskedText = styled.p`
    color: ${({ theme }) => theme.colors.black[700]};
    font-weight: 600;
    font-size: 16px;
    line-height: 32px;

    flex: 1;
`;

const ArrowIcon = styled(MaskedIcon)`
    margin-left: 12px;
    color: ${theme.colors.black[700]};
    /* flex: 0; */
` as typeof MaskedIcon;

export interface GroundingStateItem {
    groundingState: AgentGrounding;
    title: string;
    subtitle: string;
}

export const GroundingStates: Record<AgentGrounding, GroundingStateItem> = {
    [AgentGrounding.Ungrounded]: {
        title: 'Ungrounded',
        groundingState: AgentGrounding.Ungrounded,
        subtitle: 'Relies only on its own knowledge base, ignoring uploaded data',
    },
    [AgentGrounding.Hybrid]: {
        title: 'Hybrid',
        groundingState: AgentGrounding.Hybrid,
        subtitle: 'Choose for a more conversational tone, where the agent can interpret the facts',
    },
    [AgentGrounding.Grounded]: {
        title: 'Grounded',
        groundingState: AgentGrounding.Grounded,
        subtitle: 'Choose if you need your agent to stay strictly within provided knowledge',
    },
};

export const GroundingSelection = observer(() => {
    const [open, setOpen] = useState(false);
    const mixpanel = useAnalytics();
    const agentEditorStore = useAgentEditorStore();

    const onMenuClick = useCallback(() => setOpen(open => !open), []);

    const onSelect = useCallback(
        (groundingOption: AgentGrounding | null) => {
            if (!groundingOption) return;
            agentEditorStore.setLLM(llm => ({ ...llm, template: groundingOption, temperature: getDefaultTemperature(groundingOption) }));
            mixpanel?.track('agent-editor', { event: 'click', page: AgentCreateTabType.KnowledgeBase, from: groundingOption });

            setOpen(false);
        },
        [mixpanel, agentEditorStore.setLLM]
    );

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <TooltipContainer>
                <Tooltip
                    slotProps={slotProps}
                    onClose={() => setOpen(false)}
                    open={open}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    title={<GroundingList onSelect={onSelect} />}>
                    <TriggerContainer>
                        <StyledIconButton aria-label="open button" onClick={onMenuClick} ariaLabel="Canvas size" disableRipple>
                            <MaskedText>
                                <GroundingOption item={GroundingStates[agentEditorStore.llm.template || DEFAULT_AGENT_GROUNDING]} />
                            </MaskedText>
                            <ArrowIcon rotated={open} src={ArrowDown} size="2.4rem" />
                        </StyledIconButton>
                    </TriggerContainer>
                </Tooltip>
            </TooltipContainer>
        </ClickAwayListener>
    );
});
